import { Check } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { cn } from '@/shared/utils/cn'

export type SidebarStepStatus = 'complete' | 'current' | 'pending'

export interface SidebarStep {
  id: string
  label: string
  /** Completed steps that may be revisited render as links. */
  href?: string
  status: SidebarStepStatus
}

export interface SidebarStepperProps {
  steps: SidebarStep[]
  /**
   * How many steps are done, for the counter and the segmented bar. Passed in
   * rather than counted here: on the submitted screen no step is `current`, and
   * anything derived from a current-step index would silently read zero.
   */
  completedCount: number
  className?: string
}

/**
 * The "Application Progress" panel — Figma node 2098:4484.
 *
 * Desktop is the full nine-step timeline: ringed circles joined by connectors,
 * solid behind the steps already reached and dashed ahead of them. Small screens
 * collapse to the heading, the bar and a disclosure, because nine 46px rows plus
 * their connectors is roughly 560px of chrome that would push the form itself
 * off the bottom of a phone.
 *
 * State is never carried by colour alone (SC 1.4.1). The circles and connectors
 * are decorative — every one is `aria-hidden` — and the meaning is carried by
 * `aria-current="step"` plus a visually-hidden "completed" in each finished
 * label. That is also what exempts the progress bar's 2.70:1 fill-on-track: it
 * is `aria-hidden` and the counter beside it says the same thing in words.
 */
export function SidebarStepper({ steps, completedCount, className }: SidebarStepperProps) {
  const { t } = useTranslation(['apply', 'common'])
  const [expanded, setExpanded] = useState(false)

  const currentIndex = Math.max(
    0,
    steps.findIndex((step) => step.status === 'current')
  )
  const current = steps[currentIndex]

  const heading = (
    <div className="flex flex-col gap-0.25">
      <h2 className="text-title-s text-ink-900">{t('apply:sidebar.title')}</h2>
      <p className="text-label-xs text-ink-500">
        {t('apply:sidebar.completed', { completed: completedCount, total: steps.length })}
      </p>
    </div>
  )

  // One segment per step rather than a single filled track: the design counts
  // discrete milestones, and a percentage bar would imply partial progress
  // through a step that is either done or not.
  const bar = (
    <div
      aria-hidden="true"
      className="flex h-[9px] w-full overflow-hidden rounded-full bg-hairline"
    >
      {steps.map((step, index) => (
        <span
          key={step.id}
          className={cn('h-full flex-1 bg-accent', index >= completedCount && 'opacity-0')}
        />
      ))}
    </div>
  )

  const list = (
    <ol className="flex flex-col gap-2">
      {steps.map((step, index) => {
        const isCurrent = step.status === 'current'
        const isComplete = step.status === 'complete'
        const isLast = index === steps.length - 1

        // Three distinct nodes, none of which relies on colour alone: a finished
        // step is a SOLID orange disc with a white check, the step you are on is
        // an empty disc inside a thicker orange ring, and everything ahead is a
        // thin grey outline. The disc and the ring are non-text graphics at
        // 3.43:1 and 4.88:1, so SC 1.4.11 is met; the check and the ring weight
        // are decoration on top of the visually-hidden "completed" and the
        // `aria-current="step"` below.
        const circle = (
          <span
            aria-hidden="true"
            className={cn(
              'mt-[5px] flex size-4.5 shrink-0 items-center justify-center rounded-full',
              isComplete
                ? 'border-2 border-step-complete bg-step-complete'
                : isCurrent
                  ? 'border-[3px] border-accent bg-white'
                  : 'border-2 border-step-pending bg-white'
            )}
          >
            {isComplete && <Check className="size-2 text-step-complete-fg" strokeWidth={3} />}
          </span>
        )

        // The run down to the step the citizen has reached is solid, the journey
        // ahead is dashed — which is what makes "how much is left" read at a
        // glance without leaning on the orange. The current step's own connector
        // is solid too: the design draws the line INTO where you are, not up to
        // the last thing you finished. Geometry: rows are 46px on a 62px pitch
        // and the circle is inset 5px, so the line runs from its bottom edge
        // (5 + 36) for 26px, landing on the next circle's top edge. `items-start`
        // rather than `items-center` is what keeps that inset fixed when a long
        // label wraps to a second line.
        const connector = isLast ? null : (
          <span
            aria-hidden="true"
            className={cn(
              'absolute top-[41px] left-[17px] h-[26px] w-0 border-l-2',
              isComplete || isCurrent ? 'border-accent' : 'border-dashed border-step-connector'
            )}
          />
        )

        const body = (
          <span className="mt-[5px] flex min-w-0 flex-col">
            <span className="text-caption text-ink-500">
              {t('apply:sidebar.stepNumber', { n: index + 1 })}
            </span>
            <span className="text-label-m font-bold text-text">
              {step.label}
              {isComplete && <span className="sr-only"> {t('common:a11y.completed')}</span>}
            </span>
          </span>
        )

        return (
          <li
            key={step.id}
            aria-current={isCurrent ? 'step' : undefined}
            className="relative flex min-h-[46px] items-start gap-2"
          >
            {connector}
            {isComplete && step.href ? (
              <Link to={step.href} className="flex flex-1 items-start gap-2 rounded">
                {circle}
                {body}
              </Link>
            ) : (
              <>
                {circle}
                {body}
              </>
            )}
          </li>
        )
      })}
    </ol>
  )

  return (
    <nav aria-label={t('apply:sidebar.title')} className={className}>
      {/* Desktop: the full timeline. */}
      <div className="hidden rounded-xl border border-hairline bg-card p-2 shadow-sidebar lg:block">
        {heading}
        <div className="mt-[19px]">{bar}</div>
        <div className="mt-[19px]">{list}</div>
      </div>

      {/* Mobile: heading, bar, current step, disclosure. */}
      <div className="rounded-xl border border-hairline bg-card p-2 shadow-sidebar lg:hidden">
        <div className="flex items-start justify-between gap-1">
          {heading}
          <button
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded((value) => !value)}
            className="text-label-s text-action-text underline hover:text-action-text-hover"
          >
            {expanded ? t('apply:sidebar.hideSteps') : t('apply:sidebar.allSteps')}
          </button>
        </div>
        <div className="mt-1.5">{bar}</div>
        {current && <p className="mt-1 text-label-m font-bold text-text">{current.label}</p>}
        {expanded && <div className="mt-2">{list}</div>}
      </div>
    </nav>
  )
}
